import { computed } from 'vue'

import { useSubjectResource } from '@/composables/useSubjectResource'
import IbexaContentTypeIdentifier from '@/enums/IbexaContentTypeIdentifier'
import type { IbexaImageField, IbexaRelationField } from '@/types/Ibexa'
import type { AnchorNavigationTask, StepTask } from '@/types/Task'
import { useAppRouting } from '@/composables/useAppRouting'
import RouteParam from '@/enums/RouteParam'

export const useAnchorNavigationTask = () => {
  const { getContentIdFromRouteParam } = useAppRouting()
  const collectionTaskId = getContentIdFromRouteParam(RouteParam.CollectionTask) as number
  const subjectResourceId = getContentIdFromRouteParam(RouteParam.SubjectResource) as number

  const contentId = computed(() => collectionTaskId >= 0 ? collectionTaskId : subjectResourceId)

  const taskToSection = (task: StepTask | undefined) => {
    if (!task) return

    return ({
      description: task?.description,
      isNoAidTask: task?.isNoAidTask ?? false,
      descriptionAfterSubtasks: task?.descriptionAfterSubtasks,
      image: task?.image ?? {} as IbexaImageField,
      svg: task?.svg ?? {} as IbexaRelationField,
      subtasks: task?.subtasks,
      imageAlt: task?.imageAlt ?? '',
      rightColumnRichText: task?.rightColumnRichText ?? '',
      title: task?.title,
      contentId: task?.contentId,
    })
  }

  const { resourceGuide, subjectResource, stepTasks, taskFrontpage, introductionStep } = useSubjectResource(contentId.value)

  const introSection = computed(() => taskToSection(introductionStep.value as StepTask) as AnchorNavigationTask)
  const sections = computed(() => stepTasks.value.filter(task => task?.contentId !== introSection.value?.contentId).map(taskToSection))
  const sectionTitles = computed(() => sections.value.map(task => task?.title ?? ''))
  const anchors = computed(() => Array.from(Array(sections.value.length)).map((_, i) => {
    return subjectResource.value?.isStepSuperTitle ? `Steg ${i + 1}` : `Oppgave ${i + 1}`
  }))

  const content = computed(() => ({
    description: taskFrontpage.value?.description || subjectResource.value?.description || '',
    image: taskFrontpage.value?.image ?? {} as IbexaImageField,
    anchors: anchors.value,
    introSection: introSection.value,
    introSectionLabel: subjectResource.value?.introductionStepLabel,
    sections: sections.value as AnchorNavigationTask[],
    sectionTitles: sectionTitles.value,
    contentId: subjectResource.value?.contentId ?? 0,
    locationId: subjectResource.value?.locationId ?? '',
    parentLocationId: subjectResource.value?.parentLocationId ?? '',
    title: subjectResource.value?.title ?? '',
    contentTypeIdentifier: IbexaContentTypeIdentifier.vgsSubjectResource,
    pathString: subjectResource.value?.pathString ?? '',
    isTeacherOnly: subjectResource.value?.isTeacherOnly ?? false,
    depth: subjectResource.value?.depth ?? 0,
    isStepSuperTitle: subjectResource.value?.isStepSuperTitle ?? false,
  }))

  return {
    content,
    resourceGuide,
    subjectResource,
  }
}
